/**
 * AuthorFirestoreService — read / subscribe / update for
 *   projects/{pid}/authors/{authorId}
 *
 * Multi-author invariant: exactly one author per project may have
 * isDefault:true, but that flag is a baseline/default-copy source. It is not
 * the active content-plan workspace selector; current workspace comes from the
 * Content Plan URL author param.
 *
 * - Firestore-direct (no backend endpoint) — matches ContentPublication /
 *   TopicBacklog / PostExamples precedents.
 * - Partial updates via `updateDoc` with dot-notation (per
 *   feedback_firestore_merge_bug memory).
 * - All writes through stripUndefined (Sprint 16 bug A7).
 *
 * Writes for ONBOARDING are done server-side by AuthorOnboardingHandler —
 * this service covers subsequent manual edits and subscriptions.
 */

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, onSnapshot,
  query, where, limit, serverTimestamp, writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { stripUndefined } from '../utils/stripUndefined';
import type { AuthorBasePath } from './AuthorPlatformService';

// Author entity type — generated from openapi-content-plan.yaml v1.1.0
// Imported from schema-models content-plan for type safety.
import type { Author } from '@segmently/schema-models/content-plan/models';

export type AuthorDoc = Author;

export class AuthorFirestoreService {
  /**
   * `basePath` selects between self-author (`authors`, default) and tracked
   * creator (`external_authors`) collections. Two singletons exported below.
   */
  constructor(private readonly basePath: AuthorBasePath = 'authors') {}

  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  /** Firestore collection ref for authors under this project */
  private collectionRef(projectId: string) {
    return collection(this.db, 'projects', projectId, this.basePath);
  }

  async listAuthors(projectId: string): Promise<AuthorDoc[]> {
    const snap = await getDocs(this.collectionRef(projectId));
    return snap.docs
      .map((d) => ({ ...(d.data() as AuthorDoc), id: (d.data() as AuthorDoc).id ?? d.id }))
      .sort((a, b) => String(a.displayName ?? a.id ?? '').localeCompare(String(b.displayName ?? b.id ?? '')));
  }

  subscribeAllAuthors(
    projectId: string,
    callback: (authors: AuthorDoc[]) => void,
  ): Unsubscribe {
    return onSnapshot(this.collectionRef(projectId), (snap) => {
      const authors = snap.docs
        .map((d) => ({ ...(d.data() as AuthorDoc), id: (d.data() as AuthorDoc).id ?? d.id }))
        .sort((a, b) => {
          if (Boolean(a.isDefault) !== Boolean(b.isDefault)) return a.isDefault ? -1 : 1;
          return String(a.displayName ?? a.id ?? '').localeCompare(String(b.displayName ?? b.id ?? ''));
        });
      callback(authors);
    });
  }

  async getAuthorById(projectId: string, authorId: string): Promise<AuthorDoc | null> {
    const snap = await getDoc(doc(this.db, 'projects', projectId, this.basePath, authorId));
    if (!snap.exists()) return null;
    return { ...(snap.data() as AuthorDoc), id: (snap.data() as AuthorDoc).id ?? snap.id };
  }

  /**
   * Fetch the baseline default author for a project. This is a copy-source /
   * defaults concept, not the active workspace selector.
   */
  async getDefaultAuthor(projectId: string): Promise<AuthorDoc | null> {
    const q = query(
      this.collectionRef(projectId),
      where('isDefault', '==', true),
      limit(1),
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const docSnap = snap.docs[0]!;
    return { ...(docSnap.data() as AuthorDoc), id: (docSnap.data() as AuthorDoc).id ?? docSnap.id };
  }

  /**
   * Real-time subscription to the project's baseline default author.
   * Callback receives null when no default author exists.
   */
  subscribeDefaultAuthor(
    projectId: string,
    callback: (author: AuthorDoc | null) => void,
  ): Unsubscribe {
    const q = query(
      this.collectionRef(projectId),
      where('isDefault', '==', true),
      limit(1),
    );
    return onSnapshot(q, (snap) => {
      if (snap.empty) {
        callback(null);
      } else {
        const docSnap = snap.docs[0]!;
        callback({ ...(docSnap.data() as AuthorDoc), id: (docSnap.data() as AuthorDoc).id ?? docSnap.id });
      }
    });
  }

  async setDefaultAuthor(projectId: string, authorId: string): Promise<void> {
    const authorsSnap = await getDocs(this.collectionRef(projectId));
    if (!authorsSnap.docs.some((d) => d.id === authorId)) {
      throw new Error(`Author ${authorId} not found in project ${projectId}`);
    }
    const batch = writeBatch(this.db);
    for (const authorDoc of authorsSnap.docs) {
      batch.update(authorDoc.ref, {
        isDefault: authorDoc.id === authorId,
        updatedAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }

  /**
   * Partial update of an author doc. Uses updateDoc (NOT setDoc merge:true)
   * to avoid stale nested-key leakage.
   */
  async updateAuthor(
    projectId: string,
    authorId: string,
    patch: Partial<AuthorDoc>,
  ): Promise<void> {
    const ref = doc(this.db, 'projects', projectId, this.basePath, authorId);
    const cleaned = stripUndefined({ ...patch, updatedAt: serverTimestamp() });
    await updateDoc(ref, cleaned as Record<string, unknown>);
  }

  /**
   * Idempotent upsert — used for manual author creation flows (rare in Phase
   * A; onboarding handler is the primary writer).
   */
  async upsertAuthor(
    projectId: string,
    authorId: string,
    data: Partial<AuthorDoc>,
  ): Promise<void> {
    const ref = doc(this.db, 'projects', projectId, this.basePath, authorId);
    await setDoc(
      ref,
      stripUndefined({
        id: authorId,
        projectId,
        updatedAt: serverTimestamp(),
        ...data,
      }),
      { merge: false },
    );
  }
}

export const authorFirestoreService = new AuthorFirestoreService('authors');
export const externalAuthorFirestoreService = new AuthorFirestoreService('external_authors');

/** Pick the right singleton given a basePath. Falls back to self-author. */
export function getAuthorFirestoreService(
  basePath?: AuthorBasePath,
): AuthorFirestoreService {
  return basePath === 'external_authors'
    ? externalAuthorFirestoreService
    : authorFirestoreService;
}
